import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = { class: "pricing-page" }
const _hoisted_2 = { class: "pricing-shell" }
const _hoisted_3 = { class: "toolbar" }
const _hoisted_4 = { class: "patient-picker" }
const _hoisted_5 = {
  key: 0,
  class: "patient-results"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "summary-grid" }
const _hoisted_9 = { class: "content-grid" }
const _hoisted_10 = { class: "side-panel" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { key: 0 }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "plan-panel" }
const _hoisted_15 = { class: "add-row" }
const _hoisted_16 = { class: "table-area" }
const _hoisted_17 = ["onUpdate:modelValue"]
const _hoisted_18 = ["onUpdate:modelValue"]
const _hoisted_19 = ["onUpdate:modelValue"]
const _hoisted_20 = ["onUpdate:modelValue"]
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { key: 0 }
const _hoisted_23 = { class: "memo" }

import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

type Patient = { id?: number; chartNo: string; name: string; phone?: string };
type PlanItem = { id: number; tooth: string; name: string; price: number; memo: string };
type TreatmentPlan = { id: number; chartNo: string; patientName: string; date: string; title: string; items: PlanItem[]; subtotal: number; vat: number; total: number; doctor: string; counselor: string; memo: string; status: '작성' | '제출' };

const STORAGE_KEY = 'bioa-treatment-plans';

export default /*@__PURE__*/_defineComponent({
  __name: 'PricingPage',
  setup(__props) {

const router = useRouter();
const patients = ref<Patient[]>(loadPatients());
const plans = ref<TreatmentPlan[]>(readStorage<TreatmentPlan[]>(STORAGE_KEY, []));
const currentPatient = ref<Patient | null>(patients.value[0] || null);
const patientQuery = ref(currentPatient.value ? `${currentPatient.value.name} ${currentPatient.value.chartNo}` : '');
const patientListOpen = ref(false);
const selectedPlanId = ref<number | null>(null);
const customItem = reactive({ name: '', price: 0 });
const editing = reactive<TreatmentPlan>(blankPlan());

const catalogItems: Array<{ name: string; price: number; tooth: string }> = [];

const filteredPatients = computed(() => {
  const q = patientQuery.value.trim().toLowerCase();
  return patients.value.filter((p) => [p.name, p.chartNo, p.phone].join(' ').toLowerCase().includes(q)).slice(0, 40);
});
const patientPlans = computed(() => plans.value.filter((plan) => !currentPatient.value || plan.chartNo === currentPatient.value.chartNo).sort((a, b) => b.id - a.id));
const selectedPlan = computed(() => plans.value.find((plan) => plan.id === selectedPlanId.value) || null);
const subtotal = computed(() => editing.items.reduce((sum, item) => sum + Number(item.price || 0), 0));
const vat = computed(() => Math.round(subtotal.value * 0.1));
const total = computed(() => subtotal.value + vat.value);

function selectPatient(patient: Patient) {
  currentPatient.value = patient;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
  newPlan();
}

function blankPlan(): TreatmentPlan {
  return {
    id: 0,
    chartNo: currentPatient.value?.chartNo || '',
    patientName: currentPatient.value?.name || '',
    date: today(),
    title: '치료비 계획',
    items: [],
    subtotal: 0,
    vat: 0,
    total: 0,
    doctor: '김원장',
    counselor: '데스크',
    memo: '',
    status: '작성',
  };
}

function newPlan() {
  selectedPlanId.value = null;
  Object.assign(editing, blankPlan());
}

function loadPlan(plan: TreatmentPlan) {
  selectedPlanId.value = plan.id;
  Object.assign(editing, clonePlan(plan));
}

function addCatalogItem(item: { name: string; price: number; tooth: string }) {
  editing.items.push({ id: Date.now() + Math.random(), tooth: item.tooth, name: item.name, price: item.price, memo: '' });
}

function addCustomItem() {
  if (!customItem.name.trim()) return;
  editing.items.push({ id: Date.now() + Math.random(), tooth: '', name: customItem.name, price: Number(customItem.price || 0), memo: '' });
  customItem.name = '';
  customItem.price = 0;
}

function removeItem(id: number) {
  editing.items = editing.items.filter((item) => item.id !== id);
}

function savePlan(status: '작성' | '제출' = '작성') {
  if (!currentPatient.value) return alert('환자를 선택하세요.');
  if (!editing.items.length) return alert('치료 항목을 추가하세요.');
  const payload: TreatmentPlan = {
    ...clonePlan(editing),
    id: selectedPlanId.value || Date.now(),
    chartNo: currentPatient.value.chartNo,
    patientName: currentPatient.value.name,
    date: today(),
    subtotal: subtotal.value,
    vat: vat.value,
    total: total.value,
    status,
  };
  const index = plans.value.findIndex((plan) => plan.id === payload.id);
  if (index >= 0) plans.value[index] = payload;
  else plans.value.push(payload);
  selectedPlanId.value = payload.id;
  Object.assign(editing, clonePlan(payload));
  persist();
  return payload;
}

function submitPlan() {
  const saved = savePlan('제출');
  if (!saved) return;
  router.push({ path: '/plan', query: { chartNo: saved.chartNo, planId: String(saved.id) } });
}

function deletePlan() {
  if (!selectedPlan.value || !window.confirm('선택한 치료비 계획을 삭제할까요?')) return;
  plans.value = plans.value.filter((plan) => plan.id !== selectedPlanId.value);
  persist();
  newPlan();
}

function clonePlan(plan: TreatmentPlan): TreatmentPlan {
  return { ...plan, items: plan.items.map((item) => ({ ...item, price: Number(item.price || 0) })) };
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans.value));
}

function money(value: number) {
  return `${Math.round(value || 0).toLocaleString('ko-KR')}원`;
}

function loadPatients(): Patient[] {
  const stored = readStorage<Patient[]>('bioa-patients', []);
  if (stored.length) return stored.map((p) => ({ chartNo: p.chartNo || `P-${p.id || 'TEMP'}`, name: p.name || '환자', phone: p.phone || '' }));
  return [];
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

if (currentPatient.value) newPlan();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _cache[23] || (_cache[23] = _createElementVNode("nav", { class: "tabs" }, [
        _createElementVNode("button", { class: "active" }, "가격 책정 치료비 계획")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          _cache[8] || (_cache[8] = _createElementVNode("span", null, "환자", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((patientQuery).value = $event)),
            placeholder: "이름/차트/전화 검색",
            onFocus: _cache[1] || (_cache[1] = ($event: any) => (patientListOpen.value = true))
          }, null, 544), [
            [_vModelText, patientQuery.value]
          ]),
          (patientListOpen.value && patientQuery.value.trim())
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPatients.value, (patient) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: patient.chartNo,
                    type: "button",
                    onClick: ($event: any) => (selectPatient(patient))
                  }, [
                    _createElementVNode("strong", null, _toDisplayString(patient.name), 1),
                    _createElementVNode("span", null, _toDisplayString(patient.chartNo) + " · " + _toDisplayString(patient.phone || '-'), 1)
                  ], 8, _hoisted_6))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("button", { onClick: newPlan }, "새 계획"),
        _createElementVNode("button", { onClick: savePlan }, "저장"),
        _createElementVNode("button", {
          class: "primary",
          onClick: submitPlan
        }, "제출"),
        _createElementVNode("button", {
          disabled: !selectedPlan.value,
          onClick: deletePlan
        }, "계획 삭제", 8, _hoisted_7)
      ]),
      _createElementVNode("section", _hoisted_8, [
        _createElementVNode("article", null, [
          _cache[9] || (_cache[9] = _createElementVNode("span", null, "환자", -1)),
          _createElementVNode("strong", null, _toDisplayString(currentPatient.value?.name || '미선택'), 1)
        ]),
        _createElementVNode("article", null, [
          _cache[10] || (_cache[10] = _createElementVNode("span", null, "치료비 합계", -1)),
          _createElementVNode("strong", null, _toDisplayString(money(subtotal.value)), 1)
        ]),
        _createElementVNode("article", null, [
          _cache[11] || (_cache[11] = _createElementVNode("span", null, "부가세", -1)),
          _createElementVNode("strong", null, _toDisplayString(money(vat.value)), 1)
        ]),
        _createElementVNode("article", null, [
          _cache[12] || (_cache[12] = _createElementVNode("span", null, "총 계약금액", -1)),
          _createElementVNode("strong", null, _toDisplayString(money(total.value)), 1)
        ])
      ]),
      _createElementVNode("section", _hoisted_9, [
        _createElementVNode("aside", _hoisted_10, [
          _createElementVNode("section", null, [
            _cache[13] || (_cache[13] = _createElementVNode("header", null, "저장된 치료비 계획", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(patientPlans.value, (plan) => {
              return (_openBlock(), _createElementBlock("button", {
                key: plan.id,
                type: "button",
                class: _normalizeClass({ selected: selectedPlanId.value === plan.id }),
                onClick: ($event: any) => (loadPlan(plan))
              }, [
                _createElementVNode("strong", null, _toDisplayString(plan.title), 1),
                _createElementVNode("span", null, _toDisplayString(plan.date) + " · " + _toDisplayString(money(plan.total)), 1)
              ], 10, _hoisted_11))
            }), 128)),
            (!patientPlans.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_12, "저장된 계획이 없습니다."))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("section", null, [
            _cache[14] || (_cache[14] = _createElementVNode("header", null, "치료 항목 카탈로그", -1)),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(catalogItems, (catalog) => {
              return _createElementVNode("button", {
                key: catalog.name,
                type: "button",
                onClick: ($event: any) => (addCatalogItem(catalog))
              }, [
                _createElementVNode("strong", null, _toDisplayString(catalog.name), 1),
                _createElementVNode("span", null, _toDisplayString(money(catalog.price)), 1)
              ], 8, _hoisted_13)
            }), 64))
          ])
        ]),
        _createElementVNode("section", _hoisted_14, [
          _createElementVNode("header", null, [
            _cache[16] || (_cache[16] = _createElementVNode("strong", null, "치료 계획 작성", -1)),
            _createElementVNode("label", null, [
              _cache[15] || (_cache[15] = _createElementVNode("span", null, "제목", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editing.title) = $event))
              }, null, 512), [
                [_vModelText, editing.title]
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((customItem.name) = $event)),
              placeholder: "처치 내용"
            }, null, 512), [
              [_vModelText, customItem.name]
            ]),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((customItem.price) = $event)),
              type: "number",
              min: "0",
              placeholder: "금액"
            }, null, 512), [
              [
                _vModelText,
                customItem.price,
                void 0,
                { number: true }
              ]
            ]),
            _createElementVNode("button", { onClick: addCustomItem }, "추가")
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("table", null, [
              _cache[17] || (_cache[17] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, "치식/부위"),
                  _createElementVNode("th", null, "처치 내용"),
                  _createElementVNode("th", null, "금액"),
                  _createElementVNode("th", null, "메모"),
                  _createElementVNode("th", null, "삭제")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editing.items, (item) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: item.id
                  }, [
                    _createElementVNode("td", null, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((item.tooth) = $event),
                        placeholder: "#36"
                      }, null, 8, _hoisted_17), [
                        [_vModelText, item.tooth]
                      ])
                    ]),
                    _createElementVNode("td", null, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((item.name) = $event)
                      }, null, 8, _hoisted_18), [
                        [_vModelText, item.name]
                      ])
                    ]),
                    _createElementVNode("td", null, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((item.price) = $event),
                        type: "number",
                        min: "0"
                      }, null, 8, _hoisted_19), [
                        [
                          _vModelText,
                          item.price,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ]),
                    _createElementVNode("td", null, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((item.memo) = $event)
                      }, null, 8, _hoisted_20), [
                        [_vModelText, item.memo]
                      ])
                    ]),
                    _createElementVNode("td", null, [
                      _createElementVNode("button", {
                        class: "danger",
                        onClick: ($event: any) => (removeItem(item.id))
                      }, "삭제", 8, _hoisted_21)
                    ])
                  ]))
                }), 128))
              ])
            ]),
            (!editing.items.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_22, "치료 항목을 추가하세요."))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("footer", null, [
            _createElementVNode("label", null, [
              _cache[19] || (_cache[19] = _createElementVNode("span", null, "담당의사", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editing.doctor) = $event))
              }, _cache[18] || (_cache[18] = [
                _createElementVNode("option", null, "김원장", -1),
                _createElementVNode("option", null, "이원장", -1),
                _createElementVNode("option", null, "박원장", -1)
              ]), 512), [
                [_vModelSelect, editing.doctor]
              ])
            ]),
            _createElementVNode("label", null, [
              _cache[21] || (_cache[21] = _createElementVNode("span", null, "상담자", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editing.counselor) = $event))
              }, _cache[20] || (_cache[20] = [
                _createElementVNode("option", null, "데스크", -1),
                _createElementVNode("option", null, "김실장", -1),
                _createElementVNode("option", null, "이실장", -1)
              ]), 512), [
                [_vModelSelect, editing.counselor]
              ])
            ]),
            _createElementVNode("label", _hoisted_23, [
              _cache[22] || (_cache[22] = _createElementVNode("span", null, "상담 메모", -1)),
              _withDirectives(_createElementVNode("textarea", {
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editing.memo) = $event)),
                rows: "3"
              }, null, 512), [
                [_vModelText, editing.memo]
              ])
            ])
          ])
        ])
      ])
    ])
  ]))
}
}

})